import { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";
import {
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
    alpha,
    useTheme,
} from "@mui/material";
import DateRangeIcon from '@mui/icons-material/DateRange';
import SettingsIcon from '@mui/icons-material/Settings';
import IntegrationIcon from '@mui/icons-material/Link';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import GroupsIcon from '@mui/icons-material/Groups';
import AssessmentPeriodListComponent from "../ui-components/AssessmentPeriodListComponent";
import ConditionalDisplay from "../components/ConditionalDisplay";
import { useMainDisplayHeight } from "../components/WindowDimensions";
import OrganizationSettingsComponent from "../ui-components/OrganizationSettingsComponent";
import { Securables, UpsertAction } from "../Enums";
import OnlyForRole from "../components/OnlyForRole";
import PageTitle from "../components/PageTitle";
import { I18N } from "../I18N";
import PanelSegment from "../components/PanelSegment";
import MergeLinkIntegration from "../components/integrations/MergeLinkIntegration";
import useViewportMode from "../hooks/useViewportMode";
import ProficiencyGroupManagementPage from './ProficiencyGroupManagementPage';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import WorkflowGroupManagement from './GroupManagementDisplay';

enum MenuItem {
    AssessmentPeriods,
    OrgSettings,
    Integrations,
    ProficiencyGroups,
    WorkflowGroups,
}

const DEFAULT_NAVIGATION_WIDTH = 280;
const MIN_NAVIGATION_WIDTH = 220;
const MAX_NAVIGATION_WIDTH = 420;
const NAVIGATION_WIDTH_STORAGE_KEY = 'organization-settings-navigation-width';

const menuDetails = {
    [MenuItem.AssessmentPeriods]: {
        title: 'Assessment Periods',
        description: 'Create and maintain the development windows used across your organization.',
    },
    [MenuItem.OrgSettings]: {
        title: 'Settings',
        description: 'Configure organization-wide defaults and assessment behavior.',
    },
    [MenuItem.Integrations]: {
        title: 'Integrations',
        description: 'Connect external systems and manage synchronization.',
    },
    [MenuItem.ProficiencyGroups]: {
        title: 'Proficiency Groups',
        description: 'Organize the proficiency categories used by templates and assessments.',
    },
    [MenuItem.WorkflowGroups]: {
        title: 'Workflow Groups',
        description: 'Manage future workflow group definitions and membership.',
    },
};

const clampNavigationWidth = (width: number) =>
    Math.min(MAX_NAVIGATION_WIDTH, Math.max(MIN_NAVIGATION_WIDTH, width));

const getInitialNavigationWidth = () => {
    const storedWidth = Number(window.localStorage.getItem(NAVIGATION_WIDTH_STORAGE_KEY));
    return Number.isFinite(storedWidth) && storedWidth > 0
        ? clampNavigationWidth(storedWidth)
        : DEFAULT_NAVIGATION_WIDTH;
};

const OrganizationSettingsPage = () => {
    const theme = useTheme();
    const { mode, isPhone, isTablet, isDesktop, isPortrait } = useViewportMode();
    const singlePane = isPhone || (isTablet && isPortrait);
    const tabletSplit = isTablet && !isPortrait;
    const mainDisplayHeight = useMainDisplayHeight();
    const { hasRole } = useAccessInfo();
    const splitPaneRef = useRef<HTMLDivElement | null>(null);
    const [listItemSelected, setListItemSelected] = useState<MenuItem>(() => {
        if (hasRole(Securables.orgsettings_assessmentperiods))
            return MenuItem.AssessmentPeriods;
        if (hasRole(Securables.orgsettings_settings))
            return MenuItem.OrgSettings;
        return MenuItem.ProficiencyGroups;
    });
    const [navigationWidth, setNavigationWidth] = useState(getInitialNavigationWidth);
    const [resizing, setResizing] = useState(false);
    const selectedDetail = menuDetails[listItemSelected];

    useEffect(() => {
        window.localStorage.setItem(NAVIGATION_WIDTH_STORAGE_KEY, navigationWidth.toString());
    }, [navigationWidth]);

    useEffect(() => {
        if (!resizing)
            return;

        const handlePointerMove = (event: PointerEvent) => {
            const bounds = splitPaneRef.current?.getBoundingClientRect();
            if (!bounds)
                return;

            setNavigationWidth(clampNavigationWidth(event.clientX - bounds.left));
        };
        const stopResizing = () => setResizing(false);
        const previousCursor = document.body.style.cursor;
        const previousUserSelect = document.body.style.userSelect;

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', stopResizing);
        window.addEventListener('pointercancel', stopResizing);

        return () => {
            document.body.style.cursor = previousCursor;
            document.body.style.userSelect = previousUserSelect;
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', stopResizing);
            window.removeEventListener('pointercancel', stopResizing);
        };
    }, [resizing]);

    const handleResizeKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        let nextWidth = navigationWidth;

        if (event.key === 'ArrowLeft')
            nextWidth -= 16;
        else if (event.key === 'ArrowRight')
            nextWidth += 16;
        else if (event.key === 'Home')
            nextWidth = MIN_NAVIGATION_WIDTH;
        else if (event.key === 'End')
            nextWidth = MAX_NAVIGATION_WIDTH;
        else
            return;

        event.preventDefault();
        setNavigationWidth(clampNavigationWidth(nextWidth));
    };

    const handleResizePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        setResizing(true);
    };

    const navigationItem = (
        item: MenuItem,
        icon: JSX.Element,
        primary: string,
        secondary: string,
    ) => (
        <ListItemButton
            selected={listItemSelected === item}
            onClick={() => setListItemSelected(item)}
            sx={{
                alignItems: 'flex-start',
                borderRadius: 1.5,
                mb: 0.5,
                px: 1.5,
                py: 1.25,
                '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.16),
                    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                },
            }}
        >
            <ListItemIcon sx={{ minWidth: 40, mt: 0.25 }}>{icon}</ListItemIcon>
            <ListItemText
                primary={primary}
                secondary={isDesktop ? secondary : undefined}
                primaryTypographyProps={{ fontWeight: listItemSelected === item ? 700 : 500 }}
                secondaryTypographyProps={{ sx: { mt: 0.35, lineHeight: 1.35 } }}
            />
        </ListItemButton>
    );

    return (
        <PanelSegment
            titleContent={<PageTitle title={I18N.Translate('Organization Settings')} />}
            fillHeight
        >
            <Box
                ref={splitPaneRef}
                data-testid="organization-settings-split-pane"
                data-viewport-mode={mode}
                data-layout={singlePane ? 'single-pane' : tabletSplit ? 'compact-split' : 'desktop-split'}
                sx={{
                    display: 'flex',
                    flexDirection: singlePane ? 'column' : 'row',
                    height: singlePane ? 'auto' : mainDisplayHeight,
                    minHeight: 0,
                    width: '100%',
                    overflow: singlePane ? 'visible' : 'hidden',
                    bgcolor: 'background.default',
                }}
            >
                <Paper
                    component="aside"
                    square
                    elevation={0}
                    sx={{
                        flex: singlePane ? '0 0 auto' : `0 0 ${tabletSplit ? MIN_NAVIGATION_WIDTH : navigationWidth}px`,
                        width: singlePane ? '100%' : tabletSplit ? MIN_NAVIGATION_WIDTH : navigationWidth,
                        minWidth: 0,
                        border: 0,
                        borderRadius: 0,
                        overflow: 'auto',
                        bgcolor: alpha(theme.palette.background.paper, 0.72),
                    }}
                >
                    <Box sx={{ px: isPhone ? 1.5 : 2.25, pt: isPhone ? 1.5 : 2.25, pb: singlePane ? 0.5 : 1.25 }}>
                        <Typography variant="overline" color="primary.main" fontWeight={700}>
                            Organization controls
                        </Typography>
                        {isDesktop && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                Choose an area to manage its configuration.
                            </Typography>
                        )}
                    </Box>
                    <List
                        component="nav"
                        aria-label="Organization settings sections"
                        sx={{
                            px: 1.25,
                            pb: 2,
                            display: singlePane ? 'flex' : 'block',
                            gap: singlePane ? 0.75 : 0,
                            overflowX: singlePane ? 'auto' : 'visible',
                            '& .MuiListItemButton-root': {
                                minHeight: isDesktop ? undefined : 44,
                                ...(singlePane && { flex: '0 0 auto', whiteSpace: 'nowrap' }),
                            },
                        }}
                    >
                        <OnlyForRole role={Securables.orgsettings_assessmentperiods}>
                            {navigationItem(MenuItem.AssessmentPeriods, <DateRangeIcon />, 'Assessment Periods', 'Development schedules and active periods')}
                        </OnlyForRole>
                        <OnlyForRole role={Securables.orgsettings_settings}>
                            {navigationItem(MenuItem.OrgSettings, <SettingsIcon />, 'Settings', 'Defaults and organization behavior')}
                        </OnlyForRole>
                        <OnlyForRole role={Securables.orgsettings_settings}>
                            {navigationItem(MenuItem.Integrations, <IntegrationIcon />, 'Integrations', 'Connected systems and data sync')}
                        </OnlyForRole>
                        <OnlyForRole role={Securables.profgroup}>
                            {navigationItem(MenuItem.ProficiencyGroups, <FeaturedPlayListIcon />, 'Proficiency Groups', 'Template and assessment categories')}
                        </OnlyForRole>
                        <OnlyForRole role={Securables.synergist_admin} preventOverrides>
                            {navigationItem(MenuItem.WorkflowGroups, <GroupsIcon />, 'Workflow Groups', 'System Administrator preview')}
                        </OnlyForRole>
                    </List>
                </Paper>

                {isDesktop && (
                    <Box
                        role="separator"
                        aria-label="Resize organization settings navigation"
                        aria-orientation="vertical"
                        aria-valuemin={MIN_NAVIGATION_WIDTH}
                        aria-valuemax={MAX_NAVIGATION_WIDTH}
                        aria-valuenow={navigationWidth}
                        tabIndex={0}
                        onPointerDown={handleResizePointerDown}
                        onDoubleClick={() => setNavigationWidth(DEFAULT_NAVIGATION_WIDTH)}
                        onKeyDown={handleResizeKeyDown}
                        sx={{
                            position: 'relative',
                            flex: '0 0 12px',
                            width: 12,
                            cursor: 'col-resize',
                            bgcolor: resizing ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                            outline: 'none',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: '50%',
                                width: resizing ? 3 : 1,
                                transform: 'translateX(-50%)',
                                bgcolor: resizing ? 'primary.main' : 'divider',
                                transition: 'width 120ms ease, background-color 120ms ease',
                            },
                            '&:hover::after, &:focus-visible::after': {
                                width: 3,
                                bgcolor: 'primary.main',
                            },
                        }}
                    />
                )}

                <Paper
                    component="section"
                    aria-label={`${selectedDetail.title} details`}
                    square
                    elevation={0}
                    sx={{
                        display: 'flex',
                        flex: '1 1 auto',
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        overflow: singlePane ? 'visible' : 'hidden',
                        border: 0,
                        borderRadius: 0,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h5" fontWeight={700}>{selectedDetail.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                            {selectedDetail.description}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: singlePane ? 'visible' : 'auto', p: { xs: 1.5, md: 2.5 } }}>
                        <OnlyForRole role={Securables.orgsettings_assessmentperiods}>
                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.AssessmentPeriods}>
                                <AssessmentPeriodListComponent />
                            </ConditionalDisplay>
                        </OnlyForRole>
                        <OnlyForRole role={Securables.orgsettings_settings}>
                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.OrgSettings}>
                                <OrganizationSettingsComponent mode={UpsertAction.Update} />
                            </ConditionalDisplay>
                        </OnlyForRole>
                        <OnlyForRole role={Securables.profgroup}>
                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.ProficiencyGroups}>
                                <ProficiencyGroupManagementPage embedded />
                            </ConditionalDisplay>
                        </OnlyForRole>
                        <OnlyForRole role={Securables.synergist_admin} preventOverrides>
                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.WorkflowGroups}>
                                <WorkflowGroupManagement embedded />
                            </ConditionalDisplay>
                        </OnlyForRole>
                        <OnlyForRole role={Securables.orgsettings_settings}>
                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.Integrations}>
                                <MergeLinkIntegration />
                            </ConditionalDisplay>
                        </OnlyForRole>
                    </Box>
                </Paper>
            </Box>
        </PanelSegment>
    );
};

export default OrganizationSettingsPage;
